/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useDropzone } from 'react-dropzone';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';

import Typography from '@mui/material/Typography';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { SvgIconComponent } from '@mui/icons-material';

interface DropZoneProps {
  children?: React.ReactNode;
  Icon?: SvgIconComponent;

  // maxSize in Mb
  maxSize?: number;
  acceptClick?: boolean;
  message?: React.ReactNode;
  accept?: string;
  file?: File;
  onDropFile: (file: File) => void;
}

const Dropzone = (props: DropZoneProps) => {
  const { children, Icon = CloudUploadIcon, message, accept, acceptClick = true, maxSize, onDropFile, file } = props;

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept,
    noClick: !acceptClick,
    noKeyboard: !acceptClick,
    multiple: true,
    maxSize: maxSize ? maxSize * 1024 * 1024 : undefined,
    onDrop(files) {
      const [file] = files;
      if (!file) return;
      onDropFile(file);
    },
  });

  return (
    <>
      <Box
        p={2}
        minHeight={100}
        width="100%"
        color="grey.500"
        bgcolor="grey.100"
        border="2px dashed"
        borderColor={isDragActive ? 'primary.main' : 'grey.500'}
        sx={{
          transition: 'border 0.2s ease-in-out',
          '&:focus': {
            outline: 'none',
          },
        }}
      >
        <Box {...getRootProps()} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <input {...getInputProps()} />
          <Icon sx={{ color: isDragActive ? 'primary.main' : 'grey.500', transition: 'color 0.2s ease-in-out', fontSize: '2.25rem' }} />
          {!file && (
            <Typography mt={1} variant="subtitle1" color="inherit" textAlign="center">
              {message}
              {maxSize && (
                <>
                  <br />
                  {`Tamaño máximo: ${maxSize}Mb`}
                </>
              )}
            </Typography>
          )}
          {file && (
            <Typography mt={1} variant="subtitle1" color="secondary">
              {file.name}
            </Typography>
          )}
        </Box>
        {children}
      </Box>

      {fileRejections.length > 0 && (
        <Alert severity="error">
          {fileRejections.map(({ file, errors }) => (
            <Box key={file.name}>
              {file.name}
              <Box ml={2}>
                {errors.map((e) => (
                  <Box key={e.code}>{e.message}</Box>
                ))}
              </Box>
            </Box>
          ))}
        </Alert>
      )}
    </>
  );
};

export default Dropzone;
